import React from "react";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";
import {
  formatBookingSlot,
  formatCurrency,
  formatShortId,
  formatTimestamp,
} from "../adminUtils";

const AdminCustomersTab = ({
  t,
  isActive,
  customersTotal,
  customerSearchQuery,
  setCustomerSearchQuery,
  handleCustomerSearch,
  customerSortBy,
  setCustomerSortBy,
  customerSortDir,
  setCustomerSortDir,
  customersLoading,
  customersError,
  customerProfiles,
  expandedCustomerKey,
  handleToggleCustomer,
  customerBlockReasons,
  customerActionId,
  handleCustomerReasonChange,
  handleToggleBlockCustomer,
  handleSaveBlockReason,
  handleCustomerManualBooking,
  handleResendCustomerConfirmation,
  customersPage,
  setCustomersPage,
  totalCustomerPages,
}) => (
<div
              className={`admin-tab ${
                isActive ? "admin-tab--active" : ""
              }`.trim()}
            >
              <section className="admin-section admin-customers">
                <header>
                  <div>
                    <p className="admin-section__eyebrow">
                      {t("admin.customers.eyebrow", "Customer profiles")}
                    </p>
                    <h2>{t("admin.customers.heading", "Customers")}</h2>
                  </div>
                  <div className="admin-tags">
                    <span className="admin-tag">
                      {customersTotal}{" "}
                      {t("admin.customers.count", "profiles")}
                    </span>
                  </div>
                </header>
                <div className="admin-search">
                  <input
                    type="search"
                    className="admin-input"
                    value={customerSearchQuery}
                    onChange={(event) => setCustomerSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        handleCustomerSearch();
                      }
                    }}
                    placeholder={t(
                      "admin.customers.searchPlaceholder",
                      "Search by name, email, phone, or booking ID"
                    )}
                    aria-label={t("admin.customers.searchLabel", "Search customers")}
                  />
                  <button
                    type="button"
                    className="admin-button admin-button--primary"
                    onClick={handleCustomerSearch}
                  >
                    {t("admin.actions.search", "Search")}
                  </button>
                </div>
                <div className="admin-filters">
                  <div className="admin-filters__group">
                    <label className="admin-form__label" htmlFor="customerSortBy">
                      {t("admin.customers.sortBy", "Sort by")}
                    </label>
                    <select
                      id="customerSortBy"
                      className="admin-select admin-select--compact"
                      value={customerSortBy}
                      onChange={(event) => {
                        setCustomerSortBy(event.target.value);
                        setCustomersPage(1);
                      }}
                    >
                      <option value="latest">
                        {t("admin.customers.sort.latest", "Latest booking")}
                      </option>
                      <option value="created">
                        {t("admin.customers.sort.created", "Account created")}
                      </option>
                      <option value="spent">
                        {t("admin.customers.sort.spent", "Total spent")}
                      </option>
                      <option value="refunds">
                        {t("admin.customers.sort.refunds", "Refund total")}
                      </option>
                      <option value="bookings">
                        {t("admin.customers.sort.bookings", "Booking count")}
                      </option>
                      <option value="noshow">
                        {t("admin.customers.sort.noshow", "No-shows")}
                      </option>
                    </select>
                  </div>
                  <div className="admin-filters__group admin-filters__group--compact">
                    <label className="admin-form__label" htmlFor="customerSortDir">
                      {t("admin.customers.sortDir", "Order")}
                    </label>
                    <button
                      type="button"
                      id="customerSortDir"
                      className="admin-icon-toggle admin-icon-toggle--active"
                      onClick={() => {
                        setCustomerSortDir((current) =>
                          current === "asc" ? "desc" : "asc"
                        );
                        setCustomersPage(1);
                      }}
                      aria-label={t("admin.customers.sortDir", "Order")}
                      title={
                        customerSortDir === "asc"
                          ? t("admin.customers.sort.asc", "Ascending")
                          : t("admin.customers.sort.desc", "Descending")
                      }
                    >
                      {customerSortDir === "asc" ? (
                        <FiArrowUp aria-hidden="true" />
                      ) : (
                        <FiArrowDown aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
                {customersLoading ? (
                  <p className="admin-empty">
                    {t("admin.customers.loading", "Loading customer profiles...")}
                  </p>
                ) : customersError ? (
                  <p className="admin-empty">{customersError}</p>
                ) : customerProfiles.length === 0 ? (
                  <p className="admin-empty">
                    {t("admin.customers.empty", "No customer profiles yet.")}
                  </p>
                ) : (
                  <>
                    <div className="admin-customers__grid">
                      {customerProfiles.map((customer) => {
                        const currency = customer.currency || "EUR";
                        const isExpanded = expandedCustomerKey === customer.key;
                        const recentBookings = Array.isArray(customer.recentBookings)
                          ? customer.recentBookings
                          : [];
                        const reasonValue = customerBlockReasons[customer.key] || "";
                        const reasonDirty =
                          reasonValue.trim() !== (customer.blockReason || "").trim();
                        return (
                          <article
                            key={customer.key}
                            className={`admin-customer ${
                              customer.isBlocked ? "admin-customer--blocked" : ""
                            }`.trim()}
                          >
                            <div className="admin-customer__header">
                              <div className="admin-customer__identity">
                                <h3>{customer.name}</h3>
                                <p className="admin-customer__contact">
                                  {customer.email || t("admin.customers.none", "—")} ·{" "}
                                  {customer.phone || t("admin.customers.none", "—")}
                                </p>
                              </div>
                              <div className="admin-customer__meta">
                                <div className="admin-tags">
                                  {customer.isBlocked && (
                                    <span className="admin-tag admin-tag--blocked">
                                      {t("admin.customers.blocked", "Blocked")}
                                    </span>
                                  )}
                                  <span className="admin-tag">
                                    {customer.bookingCount}{" "}
                                    {t("admin.customers.bookings", "bookings")}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="admin-button admin-button--ghost"
                                  onClick={() => handleToggleCustomer(customer.key)}
                                >
                                  {isExpanded
                                    ? t("admin.actions.hideDetails", "Hide details")
                                    : t("admin.actions.viewDetails", "View details")}
                                </button>
                              </div>
                            </div>
                            <div className="admin-customer__stats">
                              <div>
                                <span>{t("admin.customers.totalSpent", "Total spent")}</span>
                                <strong>{formatCurrency(customer.totalSpent, currency)}</strong>
                              </div>
                              <div>
                                <span>{t("admin.customers.refunds", "Refund history")}</span>
                                <strong>{formatCurrency(customer.refundedTotal, currency)}</strong>
                              </div>
                              <div>
                                <span>{t("admin.customers.noShows", "No-show count")}</span>
                                <strong>{customer.noShowCount}</strong>
                              </div>
                              <div>
                                <span>{t("admin.customers.created", "Account created")}</span>
                                <strong>
                                  {formatTimestamp(customer.accountCreatedUtc)}
                                </strong>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="admin-customer__details">
                                <div className="admin-customer__history">
                                  <h4>{t("admin.customers.history", "Booking history")}</h4>
                                  {recentBookings.length === 0 ? (
                                    <p className="admin-empty">
                                      {t(
                                        "admin.customers.historyEmpty",
                                        "No bookings yet."
                                      )}
                                    </p>
                                  ) : (
                                    <ul>
                                      {recentBookings.map((booking) => (
                                        <li key={booking.id}>
                                          <div className="admin-customer__booking-meta">
                                            <span className="admin-customer__booking-id">
                                              {formatShortId(booking.id)}
                                            </span>
                                            <span className="admin-customer__booking-time">
                                              {formatBookingSlot(
                                                booking.startUtc,
                                                booking.endUtc
                                              )}
                                            </span>
                                          </div>
                                          <div className="admin-customer__booking-metrics">
                                            <span className="admin-customer__booking-status">
                                              {booking.status}
                                            </span>
                                            <span className="admin-customer__booking-amount">
                                              {formatCurrency(
                                                booking.totalPaid,
                                                booking.currency || currency
                                              )}
                                            </span>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div className="admin-customer__reason">
                                  <label
                                    className="admin-form__label"
                                    htmlFor={`block-reason-${customer.key}`}
                                  >
                                    {t("admin.customers.blockReason", "Block reason")}
                                  </label>
                                  <input
                                    id={`block-reason-${customer.key}`}
                                    className="admin-input admin-input--compact"
                                    value={reasonValue}
                                    onChange={(event) =>
                                      handleCustomerReasonChange(
                                        customer.key,
                                        event.target.value
                                      )
                                    }
                                    maxLength={200}
                                    placeholder={t(
                                      "admin.customers.blockReasonPlaceholder",
                                      "Note for audit log"
                                    )}
                                  />
                                </div>
                                <div className="admin-customer__actions">
                                  <button
                                    type="button"
                                    className="admin-button admin-button--outline"
                                    onClick={() => handleToggleBlockCustomer(customer)}
                                    disabled={customerActionId === customer.key}
                                  >
                                    {customer.isBlocked
                                      ? t("admin.customers.unblock", "Unblock user")
                                      : t("admin.customers.block", "Block abusive user")}
                                  </button>
                                  {customer.isBlocked && (
                                    <button
                                      type="button"
                                      className="admin-button admin-button--outline"
                                      onClick={() => handleSaveBlockReason(customer)}
                                      disabled={
                                        customerActionId === customer.key || !reasonDirty
                                      }
                                    >
                                      {t("admin.customers.saveReason", "Save reason")}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="admin-button admin-button--outline"
                                    onClick={() => handleCustomerManualBooking(customer)}
                                    disabled={customerActionId === customer.key}
                                  >
                                    {t(
                                      "admin.customers.manualBooking",
                                      "Manually create booking"
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-button admin-button--primary"
                                    onClick={() => handleResendCustomerConfirmation(customer)}
                                    disabled={customerActionId === customer.key}
                                  >
                                    {customerActionId === customer.key
                                      ? t("admin.customers.resending", "Resending...")
                                      : t(
                                          "admin.customers.resend",
                                          "Resend confirmations"
                                        )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </article>
                        );
                      })}
                    </div>
                    <div className="admin-customers__pagination">
                      <button
                        type="button"
                        className="admin-button admin-button--ghost"
                        onClick={() =>
                          setCustomersPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={customersPage <= 1 || customersLoading}
                        aria-label={t("admin.actions.prev", "Prev")}
                      >
                        {"<"}
                      </button>
                      <span className="admin-money__page">
                        {t("admin.pagination.page", "Page")} {customersPage}{" "}
                        {t("admin.pagination.of", "of")} {totalCustomerPages}
                      </span>
                      <button
                        type="button"
                        className="admin-button admin-button--ghost"
                        onClick={() =>
                          setCustomersPage((prev) =>
                            Math.min(totalCustomerPages, prev + 1)
                          )
                        }
                        disabled={
                          customersLoading || customersPage >= totalCustomerPages
                        }
                        aria-label={t("admin.actions.next", "Next")}
                      >
                        {">"}
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>
            
);

export default AdminCustomersTab;
